import { useState, useCallback } from "react";
import { ImageModal } from "./ImageModal";

interface DraggableImageFrameProps {
  image: string | null;
  alt: string;
  placeholder: string;
  onImageDrop: (dataUrl: string) => void;
}

export const DraggableImageFrame = ({ 
  image, 
  alt, 
  placeholder,
  onImageDrop 
}: DraggableImageFrameProps) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // Check if dropping from gallery (custom data)
    const galleryImageData = e.dataTransfer.getData('image/dataurl');
    if (galleryImageData) {
      onImageDrop(galleryImageData);
      return;
    }

    // Check if dropping files from computer
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(f => f.type.startsWith('image/'));
    
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        onImageDrop(dataUrl);
      };
      reader.readAsDataURL(imageFile);
    }
  }, [onImageDrop]);

  // Determine height based on placeholder
  const heightClass = placeholder === 'FLOOR PLAN' ? 'h-96' : 'h-64';

  return (
    <div
      className={`draggable-frame bg-slate-50 p-4 shadow-lg ${heightClass} flex items-center justify-center overflow-hidden transition-all ${
        isDragOver ? 'ring-4 ring-amber-400 bg-amber-50 scale-105' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {image ? (
        <ImageModal 
          src={image} 
          alt={alt} 
          className="w-full h-full object-cover object-center block cursor-pointer" 
          containerClassName="w-full h-full" 
        />
      ) : (
        <div className={`w-full h-full border-2 border-dashed flex flex-col items-center justify-center transition-colors ${
          isDragOver ? 'border-amber-400 bg-amber-100' : 'border-slate-300 bg-slate-100'
        }`}>
          <span className="text-slate-400 text-sm tracking-wide text-center px-2">
            {isDragOver ? 'DROP HERE' : placeholder}
          </span>
          {!isDragOver && (
            <span className="text-xs text-slate-400 mt-2">Drag photo here</span>
          )}
        </div>
      )}
    </div>
  );
};
